import { Alert, deletePrompt, isAndroid, isIOS, Popup, shareFile } from '@helpers';
import { UseManageInfoPopupWebParams } from './useManageInfoPopupWeb';
import InfoModal from '@v2/components/imageHandling/components/Header/components/InfoPopup/InfoModal';
import { PermissionsAndroid } from 'react-native';
import { check, openSettings, PERMISSIONS, request, RESULTS } from 'react-native-permissions';
import React, { useState, useCallback } from 'react';
import ReactNativeBlobUtil from 'react-native-blob-util';
import { CameraRoll } from '@react-native-camera-roll/camera-roll';
import i18n from 'i18n-js';
import { useSelector } from 'react-redux';
import { selectCurrentImageDocumentContext } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import ModalWrapper from '@components/modal/ModalWrapper';

interface UseManageInfoPopupTabletMobileParams extends UseManageInfoPopupWebParams {}

export const useManageInfoPopupTabletMobile = ({
  C,
  deleteImage,
  images,
  selectedIndex,
  setSelectedIndex,
  font,
  list: listFromProps,
  scale,
}: UseManageInfoPopupTabletMobileParams) => {
  const currentImageContext = useSelector(selectCurrentImageDocumentContext);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);

  const openInfoModal = () => {
    Popup({
      Component: (p: any) => <InfoModal {...{ modalInfoVisible: true, triggerInfoModal: p.hide, C, images, selectedIndex, scale, font }} />,
    });
  };

  // const openInfoModal = useCallback(() => {
  //   setIsInfoModalVisible(true);
  // }, []);

  const closeInfoModal = useCallback(() => {
    setIsInfoModalVisible(false);
  }, []);

  const renderInfoModal = () => {
    if (!isInfoModalVisible) return null;

    return (
      <ModalWrapper isVisible={isInfoModalVisible} onBackdropPress={closeInfoModal} C={C}>
        <InfoModal {...{ modalInfoVisible: true, triggerInfoModal: closeInfoModal, C, images, selectedIndex, scale, font }} />
      </ModalWrapper>
    );
  };

  async function hasAndroidPermission() {
    const permission = PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE;

    const hasPermission = await PermissionsAndroid.check(permission);
    if (hasPermission) {
      return true;
    }

    const status = await PermissionsAndroid.request(permission);
    return status === 'granted';
  }

  const hasIosPermission = async () => {
    const permission = PERMISSIONS.IOS.PHOTO_LIBRARY;

    const hasPermission = await check(permission);

    if (hasPermission === RESULTS.GRANTED) {
      return true;
    }

    if (hasPermission === RESULTS.BLOCKED || hasPermission === RESULTS.UNAVAILABLE) {
      Alert({
        title: 'Access to the gallery is required',
        actions: [
          {
            text: 'Cancel',
            onPress: systemAlert.hide, // this is global value
            style: 'primary',
          },
          {
            text: 'Go to Settings',
            onPress: openSettings,
            style: 'primary',
          },
        ],
      });
      return false;
    }

    if (hasPermission === RESULTS.DENIED) {
      const status = await request(permission);
      return status === RESULTS.GRANTED;
    }

    return false;
  };

  const saveToCameraRoll = async () => {
    if (isAndroid && !(await hasAndroidPermission())) {
      return;
    }

    if (isIOS && !(await hasIosPermission())) {
      return;
    }

    try {
      const url = images[selectedIndex].originUrl;
      if (!url) return;

      const fileExtension = url.split('.').pop()?.split('?')[0];

      const resp = await ReactNativeBlobUtil.config({
        fileCache: true,
        appendExt: fileExtension,
      }).fetch('GET', url, { ignoreInterceptor: 'true' });

      const imagePath = resp.path();

      const fileUrl = isAndroid ? `file://${imagePath}` : imagePath;

      await CameraRoll.saveAsset(fileUrl);
      Alert({ title: 'Success' });
    } catch (error) {
      Alert({ title: i18n.t('Error'), text: i18n.t('Something didn’t work') });
    }
  };

  const list = listFromProps ?? [
    {
      title: i18n.t('Save to Photos'),
      icon: 'save',
      onPress: saveToCameraRoll,
      color: 'Label/Light/Primary',
      width: 14,
      height: 18,
    },
    {
      title: i18n.t('Share'),
      icon: 'share',
      onPress: () => shareFile({ setLoading: () => {}, images, selectedIndex }),
      color: 'Label/Light/Primary',
      width: 15,
      height: 18,
    },
    {
      title: i18n.t('Get Info'),
      icon: 'info',
      onPress: openInfoModal,
      color: 'Label/Light/Primary',
      width: 16,
      height: 16,
    },
    {
      title: i18n.t('Delete'),
      icon: 'remove',
      onPress: () =>
        deletePrompt({ images, selectedIndex, setSelectedIndex, deleteImage, imageContext: currentImageContext, onSuccessImageRemove: () => {} }),
      color: 'Chart/Light/Warning',
      width: 15,
      height: 17,
    },
  ];

  return { list, isInfoModalVisible, closeInfoModal };
};
